// The frame of Me: [rail] | sidebar | panel. The rail exists only when the host
// gives one (cloud); the offline edition starts with the sidebar. Hosts put
// their own screens in the same frame (exported as `Frame`) with their own
// sidebar, so every place of the app has one shape.
//
// Promise: the panel keeps the width a document needs as long as it can (see
// layout.ts); below that a frame that can fold shows its folded sidebar, and
// otherwise (or at a phone's width) the sidebar becomes a drawer opened from
// the panel's header. The open drawer pushes the frame right and greys it (it
// cannot be touched; pressing it closes the drawer, as do choosing something
// in it and Escape). The rail, when there is one, is in the drawer beside the
// sidebar.
//
// The sidebar's edge is dragged to change its width (kept on this device;
// double-click returns to the default). A frame that gives a `collapsed`
// sidebar (its icons only) can also be folded to it: drag the edge past
// SIDEBAR_SNAP_BELOW, or press `]`. The width and the folding are kept on
// this device, one for every frame (Me's, a workspace's, the settings'), so
// moving between them never changes the sidebar's size; a frame without a
// folded sidebar always shows the full one and leaves the kept choice alone.
// A frame that folds only by width (`foldByWidthOnly`: the settings) cannot be
// folded by hand and does not read the kept folding: it folds only where the
// window is too narrow for its full sidebar. The folded
// sidebar's buttons that need the full one open it over the panel, the same
// drawer as compact. Resting the mouse on the folded rows (PEEK_DELAY_MS)
// opens the same drawer as a peek (for the names), which goes when the
// pointer leaves it or something in it is chosen.
//
// At a phone's width a host with the phone's screens (`extensions.phone`,
// shell/phone.tsx) puts its frame around the panel: the Dock replaces the
// drawer (`menu: "dock"`; the settings' too, `menu: "settings"`, whose index
// lists their pages), or, for the admin's pages (`menu: "drawer"`), a round
// button at the bottom right opens the same drawer as compact.
//
// A host that can hide those controls has them shown only while the sidebar is
// open (folded beside a rail, the corner is left to the folded columns;
// without a rail the folded column keeps the strip and the trameria mark in it,
// so its rows stay level with the unfolded sidebar's).
//
// When the host says window controls cover the top-left corner (title-bar.ts),
// the rail and the sidebar start below their strip (in the row, folded, and in
// the drawer or peek alike), the panel's header row starts right of them where
// they reach into the panel (compact, or beside a folded sidebar narrower than
// they are), and the strip moves the window.
import { useRouterState } from "@tanstack/react-router";
import { m } from "@trameria/i18n";
import {
  type ComponentType,
  type CSSProperties,
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMeApp } from "../app/context";
import { useDeviceSetting } from "../device/device-setting";
import {
  SIDEBAR_DEFAULT,
  SIDEBAR_MAX,
  SIDEBAR_MIN,
  SIDEBAR_SNAP_BELOW,
  sidebarCollapsedSetting,
  sidebarWidthSetting,
} from "../device/settings";
import { GLASS } from "../ui/glass";
import { ShellFrameContext } from "./frame";
import {
  leftLayout,
  PHONE_MAX,
  panelWidthOf,
  RAIL_WIDTH,
  useRightInset,
  useViewportWidth,
} from "./layout";
import { ShellOutside } from "./outside/outside";
import { ShellMenuProvider } from "./outside/source";
import type { PhoneFrameProps, PhoneMenu } from "./phone";
import { useShortcuts } from "./shortcuts";
import { SidebarLock, SidebarStripMark } from "./sidebar";
import { type TitleBarArea, useTitleBarArea } from "./title-bar";
import { WindowControlButtons } from "./window-controls";

/** The frame's top margin: above the panel, and the rail's top padding (pt-2). */
const FRAME_TOP = 8;

const notAway = () => false;

export function MeShell({
  sidebar,
  collapsed: collapsedSidebar,
  label,
  list = true,
  content: contentWidth,
  menu = "dock",
  foldByWidthOnly = false,
  bare = false,
  children,
}: {
  sidebar: ReactNode;
  /** The sidebar folded to its icons. Without it the frame never folds. */
  collapsed?: ReactNode;
  /**
   * Folds only where the window is too narrow for the full sidebar, never by
   * hand (the settings: no `]`, no folding edge, and the folded state kept for
   * Me's sidebar is not theirs).
   */
  foldByWidthOnly?: boolean;
  /** Read aloud for the sidebar when it is a drawer (default: Me). */
  label?: string;
  /** Whether its screens put a list beside a document (keeps room for one). */
  list?: boolean;
  /** The room its page needs (default: a document's; the settings' pages need less). */
  content?: number;
  /**
   * At a phone's width: the Dock (`dock`; `settings` over pages without a
   * header row), or the sidebar as a drawer opened by a round button
   * (`drawer`: the admin's pages, whose sidebar is their pages).
   */
  menu?: PhoneMenu;
  /**
   * The rail alone, and the rest of the window the page's (the desktop app's
   * place of a workspace: its sidebar and panel are the cloud's page there).
   */
  bare?: boolean;
  children: ReactNode;
}) {
  const { extensions } = useMeApp();
  const Rail = extensions.Rail;
  // Me's pages (not the settings) give way while Me lives elsewhere.
  const meAway = extensions.meAway;
  const away = (meAway?.useAway ?? notAway)();
  const pathname = useRouterState({ select: (r) => r.location.pathname });
  const mePage =
    pathname.startsWith("/me") &&
    !pathname.startsWith("/me/settings") &&
    !meAway?.stays?.(pathname);
  const AwayNote = meAway?.Note;
  const page =
    meAway && mePage ? (
      away ? (
        <meAway.Cover />
      ) : (
        <>
          {children}
          {AwayNote && <AwayNote />}
        </>
      )
    ) : (
      children
    );
  const Phone = extensions.phone?.Frame;
  const [widthPref, setWidthPref] = useDeviceSetting(sidebarWidthSetting);
  const [collapsedPref, setCollapsedPref] = useDeviceSetting(
    sidebarCollapsedSetting,
  );
  // While the edge is dragged: what it shows, kept only when let go.
  const [drag, setDrag] = useState<{
    width: number;
    collapsed: boolean;
  } | null>(null);
  // The width a sidebar as wide as its content asked for (the settings').
  const [fits, setFits] = useState<ReadonlyMap<object, number>>(new Map());
  const fitSidebar = useCallback((key: object, width: number | null) => {
    setFits((prev) => {
      if (width === null ? !prev.has(key) : prev.get(key) === width)
        return prev;
      const next = new Map(prev);
      if (width === null) next.delete(key);
      else next.set(key, width);
      return next;
    });
  }, []);
  const fitted =
    fits.size === 0
      ? null
      : Math.min(
          SIDEBAR_MAX,
          Math.max(SIDEBAR_MIN, Math.ceil(Math.max(...fits.values()))),
        );
  const foldable = collapsedSidebar !== undefined;
  const foldsByHand = foldable && !foldByWidthOnly;
  const viewport = useViewportWidth();
  const rightInset = useRightInset();
  // A host that frames the screens as a phone's at any width (the iPhone Duo
  // opened; the person, 2026-10-30): laid out as at a phone's width.
  const hostPhone = Phone !== undefined && extensions.shellHost?.phone === true;
  const rowLayout = leftLayout(
    hostPhone ? Math.min(viewport, PHONE_MAX) : viewport,
    {
      rail: Rail !== undefined,
      sidebarPref: fitted ?? drag?.width ?? widthPref,
      list,
      content: contentWidth,
      foldable,
      collapsed: foldsByHand && (drag?.collapsed ?? collapsedPref),
    },
  );
  // Beside the system's column at the right (the iPhone Duo opened), the
  // folded sidebar stands in that column under the clock, as a Dock on end,
  // and leaves the row (the person, 2026-10-24: in place of the sidebar; the
  // Workspaces' rail stays at the left). Unfolding opens it over the panel.
  const sideDock =
    foldable && !rowLayout.compact && rightInset >= SIDE_DOCK_MIN_INSET;
  const layout = sideDock
    ? { ...rowLayout, collapsed: true, byWidth: true, sidebarWidth: 0 }
    : rowLayout;
  const panelWidth = panelWidthOf(viewport, layout, Rail !== undefined);
  // The phone's frame (the Dock) around the panel, when the host has one.
  const phone =
    hostPhone ||
    (Phone !== undefined && layout.compact && viewport <= PHONE_MAX);
  // The full sidebar over the panel: opened (`drawer`, by a button or `]`) or
  // peeking (`peek`, while the mouse rests on the folded rows; it goes when the
  // pointer leaves it). Both push the frame and grey the panel.
  const [open, setOpen] = useState<"drawer" | "peek" | null>(null);
  // Still drawn while sliding back after it closes.
  const [shown, setShown] = useState(false);
  const peekTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelPeek = useCallback(() => {
    if (peekTimer.current) clearTimeout(peekTimer.current);
    peekTimer.current = null;
  }, []);
  // Folded by dragging the edge, the pointer is let go over the folded rows:
  // that is not resting on them, so no peek until the pointer moves (`held`).
  // A peek asked for meanwhile (`wanted`) starts then, unless it has left.
  const hold = useRef({ held: false, wanted: false });
  // A menu opened from the folded column (a workspace's head) or the rail stays there,
  // under the peek: no peek while it is open, so the menu is not covered (the
  // person, 2026-10-27). The next rest after it closes peeks as usual.
  const foldedColumn = useRef<HTMLDivElement>(null);
  const sideDockColumn = useRef<HTMLDivElement>(null);
  // The rail's account menu too: going from the rail's foot to its items
  // crosses the folded column (the person, 2026-11-01).
  const railColumn = useRef<HTMLDivElement>(null);
  const startPeek = useCallback(() => {
    cancelPeek();
    peekTimer.current = setTimeout(() => {
      const menuOpen = [
        foldedColumn.current,
        sideDockColumn.current,
        railColumn.current,
      ].some((column) => column?.querySelector("[data-popover]") != null);
      if (!menuOpen) setOpen((now) => now ?? "peek");
    }, PEEK_DELAY_MS);
  }, [cancelPeek]);
  // A row chosen with the mouse folds the sidebar (the peek, or the one about
  // to open) and keeps it folded while the pointer stays on the column or the
  // sliding-back sidebar (`shut`); it may peek again once the pointer has left
  // them and come back, or moved onto another row of the column (the
  // person, 2026-10-27). Watched on the window: the sidebar can slide away from
  // under a still pointer, which sends no leave.
  const shut = useRef(false);
  const drawerPanel = useRef<HTMLDivElement>(null);
  const unshut = useRef<(() => void) | null>(null);
  const shutPeek = useCallback(
    (chosen: string | null) => {
      shut.current = true;
      // Where the peek was counts as the sidebar too, after it has gone from
      // under the pointer: a row chosen beside the column leaves it there.
      const was = drawerPanel.current?.getBoundingClientRect();
      unshut.current?.();
      const onMove = (e: PointerEvent) => {
        const at = e.target instanceof Element ? e.target : null;
        // Another row of the folded column: peek from it after the usual wait.
        const row = at?.closest("[data-sidebar-collapsed] a[href]");
        if (row && row.getAttribute("href") !== chosen) {
          shut.current = false;
          unshut.current?.();
          if (e.pointerType === "mouse") startPeek();
          return;
        }
        if (at?.closest("[data-peek-zone]")) return;
        if (
          was &&
          e.clientX >= was.left &&
          e.clientX < was.right &&
          e.clientY >= was.top &&
          e.clientY < was.bottom
        )
          return;
        shut.current = false;
        unshut.current?.();
      };
      window.addEventListener("pointermove", onMove);
      unshut.current = () => {
        window.removeEventListener("pointermove", onMove);
        unshut.current = null;
      };
    },
    [startPeek],
  );
  // frontend-development: not a fetch; drops the window listener with the frame.
  useEffect(() => () => unshut.current?.(), []);
  const peek = useMemo(
    () => ({
      start(pointerType: string) {
        if (pointerType !== "mouse") return;
        if (shut.current) return;
        if (hold.current.held) {
          hold.current.wanted = true;
          return;
        }
        startPeek();
      },
      cancel() {
        hold.current.wanted = false;
        cancelPeek();
      },
    }),
    [cancelPeek, startPeek],
  );
  const holdPeekUntilMoved = useCallback(() => {
    // A peek already waiting (the rows came under the pointer mid-drag) waits
    // for the move as well.
    const pending = peekTimer.current !== null;
    cancelPeek();
    hold.current = { held: true, wanted: pending };
    let from: { x: number; y: number } | null = null;
    const onMove = (e: PointerEvent) => {
      if (!from) {
        from = { x: e.clientX, y: e.clientY };
        return;
      }
      // A few pixels: the hand letting go of the button is not a move.
      if (Math.hypot(e.clientX - from.x, e.clientY - from.y) < PEEK_HOLD_PX)
        return;
      window.removeEventListener("pointermove", onMove);
      const { wanted } = hold.current;
      hold.current = { held: false, wanted: false };
      if (wanted && e.pointerType === "mouse") startPeek();
    };
    window.addEventListener("pointermove", onMove);
  }, [startPeek, cancelPeek]);
  // frontend-development: not a fetch; drops a pending peek with the frame.
  useEffect(() => cancelPeek, [cancelPeek]);
  // frontend-development: not a fetch; keeps the drawer drawn until it has
  // slid back (the same time as the slide).
  useEffect(() => {
    if (open) {
      setShown(true);
      return;
    }
    // Nothing to slide back (also the first draw): no timer to outlive the frame.
    if (!shown) return;
    const timer = setTimeout(() => setShown(false), SLIDE_MS);
    return () => clearTimeout(timer);
  }, [open, shown]);
  const openDrawer = useCallback(() => {
    cancelPeek();
    setOpen("drawer");
  }, [cancelPeek]);
  // A menu opened from the peek (the account row, a row's menu) is drawn
  // inside it; while it is open the peek stays, wherever the pointer goes.
  const menuOpenInDrawer = () =>
    drawerPanel.current?.querySelector("[data-popover]") != null;
  const mainPanel = useRef<HTMLElement>(null);
  // Hands the focus back to the panel when the drawer closes from inside (a
  // link chosen with Enter keeps the focus inside the drawer); the panel is
  // inert until then, so it is done after it closes (the effect below).
  const focusBack = useRef(false);
  // Closed while it is still opening (a row chosen as the peek grows, the
  // pointer leaving it): left to the styles, the edge, the scrim and the
  // names jumped shut and the panel snapped back in what time the opening
  // had had. Instead every part goes back from where it is, together, over
  // at least CUT_SLIDE_MS on a gentle curve (the person, 2026-10-31). Set
  // below, where the push is known.
  const openedAt = useRef(0);
  const cutMotion = useRef<Animation[]>([]);
  const cutOpening = useRef(() => {});
  const closeDrawer = useCallback(() => {
    cancelPeek();
    focusBack.current =
      drawerPanel.current?.contains(document.activeElement) ?? false;
    cutOpening.current();
    setOpen(null);
  }, [cancelPeek]);
  // frontend-development: not a fetch; moves the focus into the opened drawer
  // (not a peek: the mouse is only passing), so Escape reaches it and Tab
  // walks its rows, and back to the panel when it closes from inside.
  useEffect(() => {
    if (open === "drawer") drawerPanel.current?.focus();
    else if (open === null && focusBack.current) {
      focusBack.current = false;
      mainPanel.current?.focus({ preventScroll: true });
    }
  }, [open]);
  const shellRef = useRef<HTMLDivElement>(null);
  const setCollapsed = useCallback(
    (on: boolean) => {
      cancelPeek();
      // Unfolding from the open drawer (its lock): the unfolded sidebar takes
      // the drawer's place as it is. The frame's push is dropped in the same
      // commit as the columns widen, so it must not slide back (that read as
      // the sidebar opening again); the transition returns after the paint.
      const shell = shellRef.current;
      if (!on && shell && shell.style.transform) {
        shell.style.transition = "none";
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            shell.style.transition = "";
          }),
        );
      }
      setOpen(null);
      setCollapsedPref(on);
    },
    [setCollapsedPref, cancelPeek],
  );
  useShortcuts([
    {
      id: "shell.toggle-sidebar",
      key: "]",
      label: () => m.shell_toggle_sidebar(),
      group: "view",
      when: () =>
        foldable && !layout.compact && (foldsByHand || layout.byWidth),
      // Folded by the window's width, there is no room to unfold: the full
      // sidebar opens over the panel instead.
      run: () => {
        cancelPeek();
        if (layout.byWidth)
          setOpen((now) => (now === "drawer" ? null : "drawer"));
        else setCollapsed(!layout.collapsed);
      },
    },
  ]);
  // Only where the sidebar is not in the row (a drawer, or folded); a phone's
  // Dock has its own menu instead.
  const canOpen =
    (layout.compact || layout.collapsed) && !(phone && menu !== "drawer");
  const isOpen = open !== null && canOpen;
  // Drawn in the same commit as the push starts, so the two move together.
  const drawn = (shown || open !== null) && canOpen;
  // The stretching drawer clips itself only while its edge moves: grown and
  // still, it clips nothing, so a menu opened in it (the account's) reaches
  // past its edge (the person, 2026-10-30). Closing puts the full clip back
  // first, read by the browser, then the clip that moves the edge back.
  const [still, setStill] = useState(false);
  const [shutting, setShutting] = useState(false);
  // frontend-development: not a fetch; times the clip with the slide.
  useLayoutEffect(() => {
    if (isOpen) {
      openedAt.current = performance.now();
      for (const motion of cutMotion.current) motion.cancel();
      setShutting(false);
      const timer = setTimeout(() => setStill(true), SLIDE_MS);
      return () => clearTimeout(timer);
    }
    setStill(false);
    const el = drawerPanel.current;
    if (el) void getComputedStyle(el).clipPath;
    setShutting(true);
  }, [isOpen]);
  const windowControls = useTitleBarArea(extensions.titleBar);
  // The controls at the top-left corner (the mac traffic lights). Those at the
  // right (Windows, Linux) take nothing from the columns: header rows under
  // them keep their own end free (`useTitleBarClear`).
  const controls =
    windowControls && windowControls.left > 0 ? windowControls : null;
  // A host that can hide the window controls (the mac traffic lights) shows
  // them only while the sidebar is open (in the row, a peek or the drawer):
  // folded, they are hidden and the folded columns keep no room for them.
  // Beside a rail they always show: the rail leaves room for them (the
  // person, 2026-10-27).
  const setControlsVisible = extensions.titleBar?.setControlsVisible;
  const canHideControls =
    setControlsVisible !== undefined &&
    !Rail &&
    controls !== null &&
    layout.collapsed &&
    !layout.compact;
  const controlsHidden = canHideControls && !drawn;
  const titleBar = controlsHidden ? null : controls;
  // The host slides the controls in with the drawer and out as it closes, so
  // they are told to go when the close starts, not when the drawer is gone
  // (the layout above keeps their strip until then).
  const controlsOut = canHideControls && !isOpen;
  // frontend-development: not a fetch; tells the host whether to show the
  // window controls, and shows them again when the frame goes.
  useEffect(() => {
    setControlsVisible?.(!controlsOut);
  }, [setControlsVisible, controlsOut]);
  useEffect(() => () => setControlsVisible?.(true), [setControlsVisible]);
  // The rail and the sidebar start below the window controls' strip: both
  // move down by the same amount, so their first rows stay level.
  // Without a rail (the offline edition), the folded column keeps the same
  // strip with the controls hidden, so its rows never move between folded,
  // peek and unfolded; the strip holds the trameria mark instead (the person,
  // 2026-10-26). With a rail the folded columns take the corner as before.
  const foldedStrip = controlsHidden && !Rail ? controls : null;
  const strip = titleBar ?? foldedStrip;
  const lift = strip ? Math.max(0, strip.height - FRAME_TOP) : 0;
  // What of the covered corner reaches into the panel: all of it in compact
  // (the panel is at the window's edge), the part past a folded sidebar
  // narrower than the controls. The panel's header row moves right by that.
  // The drawer pushes the frame right by what it covers beyond the columns
  // already there, so the panel starts at its edge. From the folded column it
  // grows only to the width the sidebar has unfolded: enough for the names.
  const drawerSidebar = layout.compact ? DRAWER_SIDEBAR : (fitted ?? widthPref);
  const drawerWidth = Math.min(
    Rail ? RAIL_WIDTH + drawerSidebar : drawerSidebar,
    Math.round(viewport * 0.85),
  );
  const push = Math.max(
    0,
    drawerWidth -
      (layout.compact ? 0 : (Rail ? RAIL_WIDTH : 0) + layout.sidebarWidth),
  );
  // Beside the folded columns, a peek or the drawer pushes the panel to where
  // the unfolded sidebar would put it (and the controls show only then), so
  // the corner is measured from there: the header must not move right while
  // it peeks and back when the lock unfolds it, nor while it slides back.
  const panelLeft = layout.compact
    ? 0
    : (Rail ? RAIL_WIDTH : 0) + layout.sidebarWidth + (drawn ? push : 0);
  const cornerInset = titleBar ? Math.max(0, titleBar.left - panelLeft) : 0;
  // The lock that keeps the sidebar open: on the drawer over the folded
  // column (a peek, or opened from it), not when the window's width folded it
  // (no room to keep it open). It sits at the top right, in the window
  // controls' strip when there is one (clear of the controls at its left),
  // else over the end of the first row.
  // Just folded (by the lock, `]`, the edge): what only the folded column has
  // fades in (styles: `data-folding`). Not when the column is drawn already
  // folded (a page in another frame, a reload): nothing moved there.
  const [folding, setFolding] = useState(false);
  const wasCollapsed = useRef(layout.collapsed);
  // frontend-development: not a fetch; marks the fold itself, before it is painted
  useLayoutEffect(() => {
    const was = wasCollapsed.current;
    wasCollapsed.current = layout.collapsed;
    if (!layout.collapsed || was) return;
    setFolding(true);
    const timer = setTimeout(() => setFolding(false), SLIDE_MS + 50);
    return () => clearTimeout(timer);
  }, [layout.collapsed]);
  const lockShown =
    foldable && layout.collapsed && !layout.compact && !layout.byWidth;
  const lockInRow = lockShown && !titleBar;
  const frame = useMemo(
    () => ({
      compact: layout.compact,
      phone,
      foldable,
      collapsed: layout.collapsed,
      collapsedByWidth: layout.byWidth,
      openDrawer,
      setCollapsed,
      panelWidth,
      peek,
      cornerInset,
      lockInRow,
      fitSidebar,
    }),
    [
      layout.compact,
      phone,
      foldable,
      layout.collapsed,
      layout.byWidth,
      openDrawer,
      setCollapsed,
      panelWidth,
      peek,
      cornerInset,
      lockInRow,
      fitSidebar,
    ],
  );
  // The panel alone as wide as the window, its headers as beside an unfolded
  // sidebar (no button for one).
  const panelAloneFrame = useMemo(
    () => ({
      ...frame,
      compact: false,
      phone: false,
      foldable: false,
      collapsed: false,
      collapsedByWidth: false,
      panelWidth: viewport,
      cornerInset: 0,
      lockInRow: false,
    }),
    [frame, viewport],
  );
  const embeddedFrame = useMemo(
    () => ({
      ...frame,
      compact: true,
      phone: false,
      foldable: false,
      collapsed: false,
      collapsedByWidth: false,
      panelWidth: viewport,
      cornerInset: 0,
      lockInRow: false,
    }),
    [frame, viewport],
  );
  const columns = layout.compact
    ? "1fr"
    : `${Rail ? `${RAIL_WIDTH}px ` : ""}${sideDock ? "" : `${layout.sidebarWidth}px `}1fr`;
  // From the folded columns, the drawer is those columns stretching right:
  // its left part stands still where they are and only its right edge moves
  // (with the pushed panel), uncovering the names. Sliding the whole drawer
  // in read as a new sheet drawn over the columns. In compact there are no
  // columns to stretch, so it slides in from the window's edge.
  const stretch = !layout.compact;
  const drawerMotion: Record<string, string | undefined> = stretch
    ? {
        "--drawer-clip": `inset(0 ${push}px 0 0)`,
        // An inset while the edge moves (not none), so it moves rather than
        // jumps; none once grown and still (above).
        clipPath: isOpen
          ? still
            ? undefined
            : "inset(0 0px 0 0)"
          : `inset(0 ${shutting ? push : 0}px 0 0)`,
      }
    : {
        "--drawer-from": `${-push}px`,
        transform: isOpen ? undefined : `translateX(${-push}px)`,
      };
  cutOpening.current = () => {
    const shell = shellRef.current;
    const drawer = drawerPanel.current;
    if (!shell || !drawer || !isOpen || push <= 0) return;
    // Not where the page cannot animate (the tests' DOM).
    if (typeof shell.animate !== "function") return;
    if (performance.now() - openedAt.current >= SLIDE_MS) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    // Read every part where it is before anything is changed.
    const pushed = new DOMMatrix(getComputedStyle(shell).transform).m41;
    const opacity = (el: Element) => getComputedStyle(el).opacity;
    const parts: [HTMLElement, Keyframe[]][] = [
      [
        shell,
        [{ transform: `translateX(${pushed}px)` }, { transform: "none" }],
      ],
      [
        drawer,
        stretch
          ? [
              { clipPath: getComputedStyle(drawer).clipPath },
              { clipPath: `inset(0 ${push}px 0 0)` },
            ]
          : [
              { transform: getComputedStyle(drawer).transform },
              { transform: `translateX(${-push}px)` },
            ],
      ],
    ];
    const scrim = shell.querySelector<HTMLElement>("[data-drawer-scrim]");
    if (scrim)
      parts.push([scrim, [{ opacity: opacity(scrim) }, { opacity: 0 }]]);
    if (stretch) {
      // The cross-fade of the names and the folded column's own (styles).
      for (const el of drawer.querySelectorAll<HTMLElement>(
        "[data-fold-label]:not([data-fold-label] [data-fold-label])",
      ))
        parts.push([el, [{ opacity: opacity(el) }, { opacity: 0 }]]);
      for (const el of drawer.querySelectorAll<HTMLElement>(
        "[data-fold-ghost] [data-fold-only]",
      ))
        parts.push([el, [{ opacity: opacity(el) }, { opacity: 1 }]]);
    }
    const duration = Math.max(
      CUT_SLIDE_MS,
      SLIDE_MS * Math.min(1, pushed / push),
    );
    // The styles' transitions would win over these: off until they end.
    cutMotion.current = parts.map(([el, keyframes]) => {
      el.style.transition = "none";
      const motion = el.animate(keyframes, { duration, easing: CUT_EASE });
      const done = () => {
        el.style.transition = "";
      };
      motion.addEventListener("finish", done);
      motion.addEventListener("cancel", done);
      return motion;
    });
  };
  const marked = windowControls ? "" : undefined;
  const host = extensions.shellHost;
  const content = host ? <ShellOutside host={host}>{page}</ShellOutside> : page;
  // Embedded in a host that draws the frame: the panel alone, the whole
  // window wide (no rail, sidebar, Dock or drawer). At a phone's width the
  // panel keeps the phone's frame (its lists scroll their titles and search
  // away, keep room for the Dock and run under it) and the host draws the
  // Dock, its menu and the top Dock's buttons over it (`hostDraws`).
  // Shown inside another screen as the panel alone (the desktop app's frame
  // beside the app's own sidebar).
  const framed = extensions.frameLayout?.use() ?? null;
  if (framed?.kind === "panel")
    return (
      <ShellFrameContext.Provider value={panelAloneFrame}>
        <ShellMenuProvider>
          <main
            ref={mainPanel}
            tabIndex={-1}
            data-me-main=""
            data-shell-panel-alone=""
            className="relative flex h-dvh min-h-0 min-w-0 flex-col overflow-hidden bg-surface text-fg outline-none"
          >
            {content}
          </main>
        </ShellMenuProvider>
      </ShellFrameContext.Provider>
    );
  if (bare && !layout.compact)
    return (
      <ShellFrameContext.Provider value={frame}>
        <ShellMenuProvider>
          {windowControls && (
            <div
              aria-hidden="true"
              data-title-bar-drag=""
              className="pointer-events-none fixed inset-x-0 top-0"
              style={{ height: windowControls.height }}
            />
          )}
          <div data-me-frame="" className="h-dvh overflow-clip">
            <div
              data-me-shell=""
              data-title-bar={marked}
              className="grid h-dvh select-none overflow-hidden bg-canvas text-fg"
              style={{
                gridTemplateColumns: Rail ? `${RAIL_WIDTH}px 1fr` : "1fr",
              }}
            >
              {Rail && (
                <RailColumn rail={Rail} lift={lift} controls={titleBar} bare />
              )}
              <main
                ref={mainPanel}
                tabIndex={-1}
                data-me-main=""
                className="relative min-h-0 min-w-0 outline-none"
              >
                {content}
              </main>
            </div>
          </div>
        </ShellMenuProvider>
      </ShellFrameContext.Provider>
    );
  if (host?.embedded && !phone)
    return (
      <ShellFrameContext.Provider value={embeddedFrame}>
        <ShellMenuProvider>
          <main
            ref={mainPanel}
            tabIndex={-1}
            data-me-main=""
            data-shell-embedded=""
            className="relative flex h-dvh min-h-0 min-w-0 flex-col overflow-hidden bg-surface text-fg"
          >
            {content}
          </main>
        </ShellMenuProvider>
      </ShellFrameContext.Provider>
    );
  return (
    <ShellFrameContext.Provider value={frame}>
      <ShellMenuProvider>
        {/* First in the DOM: what comes later and can be pressed is taken out
          of the drag region (styles: `[data-title-bar]`). */}
        {/* The strip still moves the window while the controls are hidden. */}
        {windowControls && (
          <div
            aria-hidden="true"
            data-title-bar-drag=""
            className="pointer-events-none fixed inset-x-0 top-0"
            style={{ height: windowControls.height }}
          />
        )}
        {windowControls && extensions.titleBar?.windowControls && (
          <WindowControlButtons controls={extensions.titleBar.windowControls} />
        )}
        {/* For a home screen app with the columns (a tablet, the iPhone Duo
          opened): the surface at the top edge that keeps iOS from blurring
          it, of the ground's color, painting nothing (shown only there:
          styles; the phone's frame has its own). */}
        <div
          aria-hidden="true"
          data-shell-top-edge=""
          className="pointer-events-none fixed inset-x-0 top-0 z-[29] hidden h-3 bg-canvas"
        />
        {/* Clips the pushed frame, so the page never gains a sideways scroll. */}
        {/* `data-me-frame`: what the phone's keyboard keeps to the visible part
          of the screen. */}
        <div data-me-frame="" className="h-dvh overflow-clip">
          <div
            ref={shellRef}
            data-me-shell=""
            data-title-bar={marked}
            className={`grid h-dvh select-none overflow-hidden bg-canvas text-fg transition-transform duration-200 ease-out motion-reduce:transition-none ${
              layout.compact ? "" : "p-2 pl-0"
            }`}
            style={{
              gridTemplateColumns: columns,
              transform: isOpen ? `translateX(${push}px)` : undefined,
            }}
          >
            {!layout.compact && Rail && (
              <div
                ref={railColumn}
                className="contents"
                inert={isOpen || undefined}
              >
                <RailColumn rail={Rail} lift={lift} controls={titleBar} />
              </div>
            )}
            {!layout.compact && !sideDock && (
              <div
                ref={foldedColumn}
                data-sidebar-collapsed={layout.collapsed ? "" : undefined}
                data-folding={folding ? "" : undefined}
                data-peek-zone={layout.collapsed ? "" : undefined}
                onClickCapture={(e) => {
                  // A row chosen with the mouse before the peek opens: no peek
                  // until the pointer has left the column and come back.
                  if (!layout.collapsed || e.detail === 0) return;
                  const row = (e.target as HTMLElement).closest("a[href]");
                  if (!row) return;
                  cancelPeek();
                  hold.current.wanted = false;
                  shutPeek(row.getAttribute("href"));
                }}
                className="relative -my-2 flex min-h-0 flex-col py-0"
                style={lift ? { paddingTop: lift } : undefined}
                inert={isOpen || undefined}
              >
                {layout.collapsed ? collapsedSidebar : sidebar}
                {/* Kept under the drawer while it is open (its strip shows the
                  controls), so it is not drawn again, fading in anew, after
                  the drawer's own copy has faded back in. */}
                {canHideControls && !Rail && controls && layout.collapsed && (
                  <div
                    className="absolute inset-x-0 flex justify-center"
                    style={{
                      // Centered between the window's top and the first row
                      // (the avatar), not in the strip alone: there it read as
                      // sitting high (the person, 2026-10-27).
                      top: stripMarkTop(controls.height),
                    }}
                  >
                    <SidebarStripMark />
                  </div>
                )}
                {/* A sidebar as wide as its content that cannot be folded by
                  hand has nothing for its edge to do. */}
                {!layout.byWidth && (foldsByHand || fitted === null) && (
                  <SidebarEdge
                    width={layout.sidebarWidth}
                    max={layout.sidebarMax}
                    foldable={foldsByHand}
                    collapsed={layout.collapsed}
                    onDrag={setDrag}
                    onDone={(next) => {
                      setDrag(null);
                      if (foldsByHand && next.collapsed && !collapsedPref)
                        holdPeekUntilMoved();
                      if (foldsByHand) setCollapsed(next.collapsed);
                      if (!next.collapsed && fitted === null)
                        setWidthPref(next.width);
                    }}
                  />
                )}
              </div>
            )}
            {/* Always inside the phone's frame when the host has one (it draws
              nothing of its own off a phone), so crossing a phone's width
              never builds the panel again. */}
            <PhoneFrame
              frame={Phone}
              active={phone}
              menu={menu}
              openDrawer={openDrawer}
              drawerOpen={isOpen}
              hostDraws={host?.embedded}
            >
              <main
                ref={mainPanel}
                tabIndex={-1}
                data-me-main=""
                // While the drawer is drawn, the panel stacks on its own, so
                // the scrim goes over all of it (a chat's side pane is a flex
                // item with its own z, which a static one keeps) and nothing
                // of it over the drawer as it slides back.
                className={`relative flex min-h-0 min-w-0 flex-col overflow-hidden bg-surface ${drawn ? "isolate " : ""}${
                  layout.compact
                    ? drawn
                      ? "rounded-l-[16px]"
                      : ""
                    : "rounded-[10px] border border-panel-border"
                }`}
              >
                {/* Out of reach while the drawer is open (it cannot scroll or be
                  focused), not out of the layout. */}
                <div className="contents" inert={isOpen || undefined}>
                  {content}
                </div>
                {drawn && (
                  // Greys the pushed panel as it moves; pressing it closes the
                  // drawer, and moving onto it ends a peek.
                  <div
                    aria-hidden="true"
                    data-drawer-scrim=""
                    className="absolute inset-0 z-[100] bg-black/25 transition-opacity duration-200 ease-out motion-safe:animate-[drawer-scrim-in_200ms_ease-out]"
                    style={{ opacity: isOpen ? 1 : 0 }}
                    onPointerDown={closeDrawer}
                    onPointerEnter={(e) => {
                      if (
                        open === "peek" &&
                        e.pointerType === "mouse" &&
                        !menuOpenInDrawer()
                      )
                        closeDrawer();
                    }}
                  />
                )}
              </main>
            </PhoneFrame>
          </div>
        </div>
        {sideDock && (
          // Fixed on a wrapper: glass is `position: relative` (styles). The
          // column's own ground gives way to the glass.
          <div
            ref={sideDockColumn}
            data-side-dock=""
            inert={isOpen || undefined}
            className="fixed z-20 flex"
            style={{
              bottom: SIDE_DOCK_BOTTOM,
              right: SIDE_DOCK_CENTER - SIDE_DOCK_WIDTH / 2,
              maxHeight: `calc(100dvh - ${SIDE_DOCK_TOP}px - ${SIDE_DOCK_BOTTOM})`,
            }}
          >
            <div
              className={`${GLASS} flex min-h-0 flex-col overflow-hidden rounded-[22px] py-1 [&>nav]:h-auto [&>nav]:min-h-0 [&>nav]:bg-transparent`}
              style={{ width: SIDE_DOCK_WIDTH }}
            >
              {collapsedSidebar}
            </div>
          </div>
        )}
        {drawn && (
          <div
            ref={drawerPanel}
            role="dialog"
            aria-modal={open === "drawer"}
            aria-label={label ?? m.shell_me()}
            tabIndex={-1}
            data-drawer=""
            data-peek={open === "peek" ? "" : undefined}
            data-peek-zone=""
            // Grown out of the folded column: what only the full sidebar has
            // (the names, the sections) fades in as it grows and out as it
            // shrinks back (`data-closing`); styles: `data-fold-label`.
            data-grow={stretch ? "" : undefined}
            data-closing={isOpen ? undefined : ""}
            // On the drawer itself: what can be pressed inside leaves the drag
            // region, its empty top above the rail and sidebar stays in it.
            data-title-bar={marked}
            className={`fixed inset-y-0 left-0 z-30 flex bg-sidebar duration-200 ease-out motion-reduce:transition-none ${
              stretch
                ? // The push's curve (the utility `ease-out`, not the keyword), so the
                  // edge and the panel move as one.
                  "transition-[clip-path] motion-safe:animate-[drawer-grow_200ms_var(--ease-out)]"
                : "transition-transform motion-safe:animate-[drawer-in_200ms_ease-out]"
            }`}
            style={{ width: drawerWidth, ...drawerMotion } as CSSProperties}
            onPointerLeave={(e) => {
              if (open !== "peek" || e.pointerType !== "mouse") return;
              if (menuOpenInDrawer()) return;
              // Up into the window controls' strip (a drag region: the desktop
              // app stops sending pointer events there) or off the window's top
              // is not leaving the sidebar; the peek goes when the pointer
              // comes down onto the panel instead (the scrim).
              const r = e.currentTarget.getBoundingClientRect();
              const strip = windowControls ? windowControls.height : 1;
              if (e.clientY < strip && e.clientX < r.right) return;
              closeDrawer();
            }}
            onClick={(e) => {
              const row = (e.target as HTMLElement).closest("a[href]");
              if (!row) return;
              // A row chosen with the mouse in a peek also keeps it folded until
              // the pointer has left and come back, or moved to another row.
              if (open === "peek" && e.detail > 0)
                shutPeek(row.getAttribute("href"));
              closeDrawer();
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") closeDrawer();
            }}
          >
            {/* The rail comes along, so the other places stay one tap away. */}
            {Rail && (
              <RailColumn rail={Rail} lift={lift} controls={titleBar} drawer />
            )}
            <div
              className="relative flex min-h-0 min-w-0 flex-1 flex-col"
              style={lift ? { paddingTop: lift } : undefined}
            >
              {sidebar}
              {lockShown && (
                <div
                  data-fold-label=""
                  className="absolute right-2"
                  style={{
                    // Level with the folded column's mark (the person,
                    // 2026-10-27: centered on the controls it read as high);
                    // without a strip, level with the first row (its 36 px from
                    // the sidebar's 10 px top), which is the same height.
                    top: titleBar ? stripMarkTop(titleBar.height) : 12,
                  }}
                >
                  <SidebarLock />
                </div>
              )}
            </div>
            {stretch && !sideDock && collapsedSidebar && (
              // The folded column again, over the drawer where it stands, with
              // only what the full sidebar has not (the strip's mark, the
              // favorites button) shown: they fade out as the drawer grows and
              // back in as it shrinks, across the full rows fading the other
              // way, instead of vanishing under it (styles: `data-fold-ghost`).
              <div
                aria-hidden="true"
                inert
                data-fold-ghost=""
                className="pointer-events-none absolute inset-y-0 flex flex-col"
                style={{
                  left: Rail ? RAIL_WIDTH : 0,
                  width: layout.sidebarWidth,
                  paddingTop: lift || undefined,
                }}
              >
                {collapsedSidebar}
                {canHideControls && !Rail && controls && (
                  <div
                    className="absolute inset-x-0 flex justify-center"
                    style={{ top: stripMarkTop(controls.height) }}
                  >
                    <SidebarStripMark />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </ShellMenuProvider>
    </ShellFrameContext.Provider>
  );
}

/**
 * The top of a 32 px button or mark in the window controls' strip: centered
 * between the window's top and the first row, a little below the strip's
 * middle (in the strip alone it read as sitting high).
 */
function stripMarkTop(stripHeight: number): number {
  return Math.max(0, Math.round((stripHeight - 32) / 2) + 6);
}

/** The host's phone frame around the panel, or the panel as it is. */
function PhoneFrame({
  frame: Frame,
  children,
  ...props
}: PhoneFrameProps & { frame: ComponentType<PhoneFrameProps> | undefined }) {
  if (!Frame) return <>{children}</>;
  return <Frame {...props}>{children}</Frame>;
}

/**
 * The rail's column: its own shade from the top of the window to the bottom,
 * meeting the sidebar in inverted corners (styles: `[data-rail-column]`).
 */
function RailColumn({
  rail: Rail,
  lift,
  controls,
  drawer = false,
  bare = false,
}: {
  rail: ComponentType;
  /** How far below its usual top it starts (window controls above). */
  lift: number;
  /** The window controls' corner while they show over the rail. */
  controls: TitleBarArea | null;
  drawer?: boolean;
  /** In the bare frame: no padding around it to reach over. */
  bare?: boolean;
}) {
  // Over what a host lays on the columns beside it (the desktop app's frame,
  // z-20), so its corners and the strip under the controls show there as
  // beside the sidebar of its own; under the drawer (z-30).
  // The controls are wider than the rail: its ground reaches right along their
  // strip, so they sit on the rail and the sidebar starts below them (the
  // person, 2026-10-27). Styles: `[data-rail-tab]`.
  const tab = controls
    ? Math.max(0, controls.left + RAIL_TAB_PAST - RAIL_WIDTH)
    : 0;
  const tabHeight = controls ? controls.height - RAIL_TAB_RAISE : 0;
  return (
    <div
      data-rail-column=""
      data-rail-tabbed={tab ? "" : undefined}
      className={`flex min-h-0 flex-col bg-rail pt-2 ${
        drawer ? "w-[52px] flex-none" : bare ? "z-[25]" : "z-[25] -my-2"
      }`}
      style={lift ? { paddingTop: FRAME_TOP + lift } : undefined}
    >
      {tab > 0 && (
        <svg
          aria-hidden="true"
          data-rail-tab=""
          width={tab + RAIL_TAB_RADIUS}
          height={tabHeight + RAIL_TAB_RADIUS}
        >
          <path d={railTabPath(tab, tabHeight)} />
        </svg>
      )}
      <Rail />
    </div>
  );
}

/**
 * The rail's ground right of the column, as one outline from the column's
 * top-right: along the window's top, down the end, back along the bottom and
 * down into the column. Its three corners (where the end meets the window's
 * top, the end's foot, the sidebar's top-left below it) are all turned by
 * `corner`.
 */
function railTabPath(width: number, height: number): string {
  const r = RAIL_TAB_RADIUS;
  return [
    `M 0 0 H ${width + r}`,
    corner([width + r, 0], [width, 0], [width, r]),
    `V ${height - r}`,
    corner([width, height - r], [width, height], [width - r, height]),
    `H ${r}`,
    corner([r, height], [0, height], [0, height + r]),
    "Z",
  ].join(" ");
}

/**
 * A rounded corner from `a` to `b` turning at `at`: a cubic whose handles are
 * pulled RAIL_TAB_CORNER_PULL of the way toward the corner. A circle's arc
 * (0.55) left the edges between the short end's corners so short that the
 * step read as a slant; pulled further, the turn stays near the corner and
 * the edges run straight longer (the person, 2026-10-27).
 */
function corner(
  a: readonly [number, number],
  at: readonly [number, number],
  b: readonly [number, number],
): string {
  const k = RAIL_TAB_CORNER_PULL;
  const pull = (p: readonly [number, number]) =>
    `${p[0] + (at[0] - p[0]) * k} ${p[1] + (at[1] - p[1]) * k}`;
  return `C ${pull(a)} ${pull(b)} ${b[0]} ${b[1]}`;
}

/**
 * How far above the strip's bottom that ground ends: about midway between the
 * controls and the sidebar's first row (the person, 2026-10-27).
 */
const RAIL_TAB_RAISE = 6;
/**
 * How far past the controls' corner it reaches: the corner is where the
 * sidebar's 16 px icons are centered, so it ends at their right edge (at their
 * center it read as narrow: the person, 2026-10-27).
 */
const RAIL_TAB_PAST = 8;
/** Its corners' size: smaller than the sidebar's bottom corner (16). */
const RAIL_TAB_RADIUS = 12;
/** How far its corners' handles are pulled toward the corner (`corner`). */
const RAIL_TAB_CORNER_PULL = 0.75;

/** A right inset this wide or more is a column the folded sidebar can stand in. */
const SIDE_DOCK_MIN_INSET = 52;
/**
 * That Dock's width: about the clock's and the Wi-Fi mark's above it (41 pt),
 * as Safari's own round buttons in that column (44 pt). At the folded
 * sidebar's 52 it stood wider than them and off their center (the person,
 * 2026-10-24).
 */
const SIDE_DOCK_WIDTH = 44;
/** Its center from the screen's right edge: the clock's and the Wi-Fi mark's (measured). */
const SIDE_DOCK_CENTER = 48;
/**
 * Its place: at the bottom, as high as the phone's floating Dock (the person,
 * 2026-10-24: under the clock it stood too high), clear of the screen's
 * rounded corner (at 17 px up, the screen reaches 935 pt of 951; the Dock's
 * right edge is at 925). It never reaches above SIDE_DOCK_TOP, under the
 * clock and the Wi-Fi mark (they end about 100 pt down).
 */
const SIDE_DOCK_BOTTOM = "calc(var(--dock-floor) + 5px)";
const SIDE_DOCK_TOP = 120;

/** The sidebar's width in the compact drawer (the rail comes beside it). */
const DRAWER_SIDEBAR = 280;
/** How long the mouse rests on the folded sidebar before it peeks. */
const PEEK_DELAY_MS = 350;
/** How far the pointer moves, after folding by a drag, before it may peek. */
const PEEK_HOLD_PX = 4;
/** How long the drawer and the pushed frame take to slide. */
const SLIDE_MS = 200;
/** The least a close takes when it cuts the opening short, and its curve. */
const CUT_SLIDE_MS = 180;
const CUT_EASE = "cubic-bezier(0.4, 0, 0.2, 1)";

const KEY_STEP = 16;

/**
 * The sidebar's right edge: drag to change the width; past SIDEBAR_SNAP_BELOW
 * it folds (when the frame can fold) and back out it unfolds from
 * SIDEBAR_MIN. Arrow keys move it by KEY_STEP. Double-click: the default width.
 */
function SidebarEdge({
  width,
  max,
  foldable,
  collapsed,
  onDrag,
  onDone,
}: {
  width: number;
  max: number;
  foldable: boolean;
  collapsed: boolean;
  onDrag(state: { width: number; collapsed: boolean }): void;
  onDone(state: { width: number; collapsed: boolean }): void;
}) {
  const start = useRef<{ x: number; width: number } | null>(null);
  const last = useRef<{ width: number; collapsed: boolean } | null>(null);
  const at = (raw: number) =>
    foldable && raw < SIDEBAR_SNAP_BELOW
      ? { width: SIDEBAR_MIN, collapsed: true }
      : {
          width: Math.round(Math.min(max, Math.max(SIDEBAR_MIN, raw))),
          collapsed: false,
        };
  const move = (e: ReactPointerEvent) => {
    if (!start.current) return;
    last.current = at(start.current.width + e.clientX - start.current.x);
    onDrag(last.current);
  };
  const end = () => {
    if (!start.current) return;
    start.current = null;
    if (last.current) onDone(last.current);
    last.current = null;
  };
  return (
    // biome-ignore lint/a11y/useSemanticElements: a draggable splitter has no element of its own
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={m.shell_resize_sidebar()}
      aria-valuenow={width}
      aria-valuemin={foldable ? 0 : SIDEBAR_MIN}
      aria-valuemax={max}
      tabIndex={0}
      data-sidebar-edge=""
      className="group absolute inset-y-0 -right-1 z-10 w-2 cursor-col-resize touch-none outline-none"
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.currentTarget.setPointerCapture?.(e.pointerId);
        start.current = { x: e.clientX, width };
        last.current = null;
      }}
      onPointerMove={move}
      onPointerUp={end}
      onPointerCancel={end}
      onDoubleClick={() => onDone({ width: SIDEBAR_DEFAULT, collapsed: false })}
      onKeyDown={(e) => {
        if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
        e.preventDefault();
        const from = collapsed ? 0 : width;
        const step = e.key === "ArrowLeft" ? -KEY_STEP : KEY_STEP;
        onDone(
          collapsed && step > 0
            ? { width: SIDEBAR_MIN, collapsed: false }
            : at(from + step),
        );
      }}
    >
      {/* No line on hover or while held: the panel's border has round
          corners, and a straight line over it stood apart from them (the
          user's call, 2026-10-18). The cursor says it resizes; only a
          keyboard focus darkens the border, kept off the corners. */}
      <span className="absolute top-[18px] bottom-[18px] left-1 w-px group-focus-visible:bg-field-strong/60" />
    </div>
  );
}
